import type { GoalConfig } from "../../shared/goals/config";
import type { Spot } from "../../shared/goals/placements";
import type { MessageBody, MessageRole } from "../../shared/goals/messages";
import type { Plan } from "../../shared/goals/plan";
import { post, request } from "./api";
import type { GoalState } from "./goalsApi";

/** What an action is about, in the owner's words: the person, what they wrote, the people, the ad. */
export interface ActionContext {
  person?: { id: string; name: string; org: string | null; stage: string };
  inbound?: { subject: string | null; body: string | null; at: string | null };
  people?: { id: string; name: string; org: string | null }[];
  ad?: { id: string; name: string; state: string };
}

/** One thing that waits for the owner: an action only they can decide (`GET /goals/:id/needs`). */
export interface Need {
  id: string;
  kind: string;
  label: string;
  payload: Record<string, unknown>;
  check: { decision?: string; checks?: { id: string; ok: boolean; hard: boolean; text: string }[] } | null;
  runId: string | null;
  createdAt: string;
  context: ActionContext;
}

/** An action in any state (`GET /goals/:id/actions?ids=`): what was decided and what it did. */
export interface ActionView extends Need {
  state: string;
  result: Record<string, unknown> | null;
  decidedBy: string | null;
  decidedAt: string | null;
  note: string | null;
  executedAt: string | null;
}

/** A goal's numbers over the days picked, and its recent days for its pace (`GET /home/numbers`). */
export interface GoalNumbers {
  spendCents: number;
  results: number;
  clicks: number;
  impressions: number;
  recent: { day: string; results: number }[];
}

/** A goal's results so far, all as Meta counts them. */
export interface HomeResults {
  results: number;
  spendCents: number;
  clicks: number;
  asOf: string | null;
}

/** When a running goal's next review is: a local day and time in its time zone, or a moment (every N minutes). */
export type NextReview = { day: string; time: string; timezone: string } | { at: string };

export interface HomeGoal {
  id: string;
  sentence: string;
  state: GoalState;
  /** The goal's result as its version in force sets it; null before a plan is approved. */
  result: { label: string; target: number; by: string } | null;
  /** The budget and the most one result may cost, as the version in force sets them. */
  money: { budgetCents: number; perResultMaxCents: number } | null;
  version: number;
  launchedAt: string | null;
  endedAt: string | null;
  needs: number;
  /** Getting ready: what is left before launch, out of how many steps. */
  readyLeft: number | null;
  readyTotal: number | null;
  /** When the plan says the ads start, in its own words; null when Meta is off or the plan names none. */
  adsStart: { what: string; when: string } | null;
  /** A live goal's next scheduled review; null otherwise. */
  nextReview: NextReview | null;
  results: HomeResults | null;
}

export interface HomeProduct {
  id: string;
  name: string;
  logoUrl: string | null;
  /** Whether the product gets cold email. */
  coldEmail: boolean;
  goals: HomeGoal[];
}

/** The Command Center's home: every product and its goals, and what waits for the owner across them. */
export interface Home {
  products: HomeProduct[];
  needs: (Need & { goalId: string; productId: string })[];
  /** Lessons the agent proposed that wait for the owner's call. */
  lessonsWaiting: number;
}

export interface AskAnswer {
  goalId: string | null;
  candidates: { goalId: string; productId: string; sentence: string; why: string }[];
}

export interface ChatMessage {
  id: string;
  role: MessageRole;
  kind: string;
  body: MessageBody;
  runId: string | null;
  actionId: string | null;
  /** The thread a message came from, before threads merged into the goal's chat, and its title. */
  fromThreadId: string | null;
  fromThreadTitle: string | null;
  createdAt: string;
}

export interface ResultSource {
  asOf: string | null;
  ok: boolean;
  error: string | null;
}

/** Meta's numbers: spend, impressions, link clicks and Meta's own "Results". */
interface Counts {
  spendCents: number;
  impressions: number;
  clicks: number;
  results: number;
}

/** A goal's stored daily results, all from Meta: totals, each day, each ad and each plan test, and how fresh they are. */
export interface Results {
  timezone: string | null;
  totals: Counts;
  days: (Counts & { day: string })[];
  ads: (Counts & { adId: string; name: string; status: string | null; review: string | null })[];
  sources: { meta: ResultSource };
  /** Each plan test, its variants' ads added up. */
  tests: { id: string; name: string; metric: string; variants: (Counts & { variant: string; adIds: string[] })[] }[];
  /** What the agent's runs cost in the same days. */
  agentCosts: { runs: number; aiMicros: number; dataMicros: number; mediaMicros: number };
  /** The Meta campaign as it stands: its switch, daily amount and cap; null before it exists. */
  campaign: { state: string; dailyCents: number | null; spendCapCents: number | null } | null;
  email: { sent: number; replies: number; bounced: number; optedOut: number; inCampaign: number };
}

/** The dates of the Results page: one of Meta's presets Flashadz offers, or a first and last day. */
export type Dates = { preset: string } | { from: string; to: string };

/** What Explain is about: the dates, and one ad (by Flashadz's id, or Meta's for an ad added outside) or all of them. */
export type ExplainOf = ({ preset: string } | { from: string; to: string }) & { adId?: string };

/** One row of Meta's insights, every field as Meta sent it. */
export type MetaRow = Record<string, unknown>;

/** How an ad is made: the page shows video numbers only for a video, cards for a swipe ad, what people did on a post. */
export type AdShape = "video" | "photo" | "carousel" | "post";

export interface InsightAd {
  adId: string | null;
  shape: AdShape | null;
  cards: string[] | null;
  platformAdId: string;
  name: string;
  state: string | null;
  review: { status: string; at: string | null; message: { title: string | null; text: string | null; code: string | null } | null } | null;
  row: MetaRow | null;
}

/** The Results page's numbers: every field Meta reported for the dates, per ad and for the ad set. */
export interface Insights {
  range: { key: string; label: string; from: string | null; to: string | null };
  readAt: string;
  stale: boolean;
  error: string | null;
  currency: string | null;
  dropped: string[];
  set: MetaRow | null;
  ads: InsightAd[];
  /** Where the ads showed, for the ads together and per ad (rows carry `ad_id`). */
  places: { set: MetaRow[]; ads: MetaRow[] } | null;
  /** Each swipe ad's cards: Meta's rows with actions split by card. */
  cards: MetaRow[] | null;
}

export interface Version {
  id: string;
  version: number;
  state: string;
  summary: string;
  made_by: string;
  made_in: string;
  run_id: string | null;
  based_on: number | null;
  created_at: string;
  /** Whether it was ever the version in force: a plan never approved was not. */
  in_force: boolean;
}

/** One version as it was (`GET /goals/:id/versions/:n`): its setup and its plan. */
export interface VersionFull {
  id: string;
  version: number;
  state: string;
  summary: string;
  createdAt: string;
  config: GoalConfig;
  plan: Plan | null;
}

/** One run of the goal (`GET /goals/:id/runs`). */
export interface RunLine {
  id: string;
  kind: string;
  slot: string | null;
  state: string;
  trigger: string;
  messageId: string | null;
  model: string | null;
  error: string | null;
  summary: Record<string, unknown> | null;
  costMicros: { ai: number; data: number; media: number };
  byOwner: boolean;
  createdAt: string;
  startedAt: string | null;
  endedAt: string | null;
}

export interface Runs {
  runs: RunLine[];
  lastWeek: { runs: number; toolCalls: number; costMicros: { ai: number; data: number; media: number }; byKind: { kind: string; runs: number }[] };
}

/** One person the goal found (`GET /goals/:id/people`), with where each fact came from. */
export interface Person {
  id: string;
  name: string;
  org: string | null;
  market: string | null;
  role: string | null;
  tier: string | null;
  stage: string;
  profile: string | null;
  email: { address: string; page: string | null; seen: string | null; checked: string | null; verification: { result: string; tool: string; date: string } | null } | null;
  firstLine: { text: string; source: string | null } | null;
  phone: { number: string; source: string | null } | null;
  facts: { fact: string; source: string; date: string }[];
  notes: string | null;
  foundIn: string | null;
  inCampaign: boolean;
  emails: { sent: number; replies: number; lastAt: string | null };
  createdAt: string;
  updatedAt: string;
}

/** What the chat shows of Get ready: the ads, the cold emails and the lists (`GET /goals/:id/ready`). */
export interface ReadyLite {
  ads: { id: string; status: "drafting" | "needs_video" | "check" | "approved" | "launched" | "removed"; words: Record<string, unknown>; asset: Record<string, unknown> | null }[];
  emails: { step: number; subject: string; body: string; when: string; approved: boolean }[];
  lists: { market: string; state: string; people: number; withEmail: number }[];
  emailOn: boolean;
  metaOn: boolean;
}

/** One step before launch (`GET /goals/:id/ready/steps`), in the owner's words: done or not, and what is missing. */
export interface ReadyStep {
  id: string;
  label: string;
  ok: boolean;
  detail: string | null;
}

export interface Decided {
  state: string;
  outcome: unknown;
  result: Record<string, unknown> | null;
}

const goal = (id: string) => `/goals/${encodeURIComponent(id)}`;
const query = (o: Record<string, string | number | undefined>) => {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(o)) if (v !== undefined && v !== "") q.set(k, String(v));
  const s = q.toString();
  return s ? `?${s}` : "";
};

export const ccApi = {
  home: () => request<Home>("/home"),
  /** Each goal's numbers over the days picked, and its last two weeks of results (Command Center V2). */
  homeNumbers: (from: string, to: string) => request<{ goals: Record<string, GoalNumbers> }>(`/home/numbers?from=${from}&to=${to}`),
  /** Which goal a question from the Command Center belongs to; null with candidates when unsure. */
  ask: (text: string) => post<AskAnswer>("/ask", { text }),

  chat: (id: string, before?: string) => request<{ messages: ChatMessage[] }>(`${goal(id)}/chat${query({ limit: 200, before })}`),
  /** The owner writes; the agent answers in a chat run. */
  send: (id: string, text: string, results?: ExplainOf) => post<{ message: ChatMessage; runId: string }>(`${goal(id)}/chat`, { text, ...(results ? { results } : {}) }),

  needs: (id: string) => request<{ needs: Need[] }>(`${goal(id)}/needs`),
  /** Actions known by id (a review's proposals, a change in an answer), as they stand now. */
  actions: (id: string, ids: string[]) => request<{ actions: ActionView[] }>(`${goal(id)}/actions${query({ ids: ids.join(",") })}`),
  decide: (actionId: string, decision: "approve" | "skip", note?: string) =>
    post<Decided>(`/actions/${encodeURIComponent(actionId)}/decide`, note ? { decision, note } : { decision }),

  results: (id: string) => request<Results>(`${goal(id)}/results`),
  /** Every number Meta reports for the goal's ads and ad set over the dates; null before the ads are on Meta. */
  meta: (id: string, dates: Dates, refresh = false) =>
    request<{ results: Insights | null }>(`${goal(id)}/results/meta${query({ ...("preset" in dates ? { preset: dates.preset } : { from: dates.from, to: dates.to }), refresh: refresh ? "1" : undefined })}`),
  /** A person's own choices on a page: the Results page's added numbers. */
  pref: (key: string) => request<{ value: unknown }>(`/me/prefs/${encodeURIComponent(key)}`),
  setPref: (key: string, value: unknown) => request<{ value: unknown }>(`/me/prefs/${encodeURIComponent(key)}`, { method: "PUT", body: JSON.stringify({ value }) }),
  /** Newest first. `metaHolds`: the ads are on Meta, so where they show and who sees them change there first. */
  versions: (id: string) => request<{ versions: Version[]; metaHolds: boolean }>(`${goal(id)}/versions`),
  /** Bring back an older version's settings and plan, as the newest version on top of `basedOn`. */
  useVersion: (id: string, n: number, basedOn: number) => post<{ version: number }>(`${goal(id)}/versions/${n}/use`, { basedOn }),
  version: (id: string, n: number) => request<{ version: VersionFull }>(`${goal(id)}/versions/${n}`),
  /** Runs newest first; `through` (a run id) returns every run back to that one. */
  runs: (id: string, o: { limit?: number; before?: string; kind?: string; through?: string } = {}) => request<Runs>(`${goal(id)}/runs${query(o)}`),
  /** The goal's people, or only `ids`. */
  people: (id: string, o: { market?: string; stage?: string; limit?: number; offset?: number; ids?: string[] } = {}) =>
    request<{ stages: Record<string, number>; people: Person[] }>(`${goal(id)}/people${query({ ...o, ids: o.ids?.join(",") })}`),
  ready: (id: string) => request<ReadyLite>(`${goal(id)}/ready`),
  readySteps: (id: string) => request<{ steps: ReadyStep[] }>(`${goal(id)}/ready/steps`),

  /**
   * Where the goal's ads show: the chosen spots, or null for Automatic. Before the goal has a campaign it is
   * the goal's next version; once the ads are on Meta it goes to Meta first (`outcome` is the action's).
   */
  placements: (id: string, spots: Spot[] | null) =>
    post<{ outcome: string | null; version: number | null; state: string | null; result: Record<string, unknown> | null }>(`${goal(id)}/placements`, { spots }),
  /** What the owner does to a goal; Flashadz writes the chat's line about it. */
  apply: (id: string, basedOn: number, config: GoalConfig, summary: string) => post<{ version: number }>(`${goal(id)}/apply`, { basedOn, config, summary }),
  pause: (id: string) => post<{ ok: true; meta: unknown }>(`${goal(id)}/pause`),
  resume: (id: string) => post<{ ok: true; meta: unknown }>(`${goal(id)}/resume`),
  /** Edit goal on a goal that is approved but not live: the setup starts again from the version in force. */
  replan: (id: string) => post<{ ok: true }>(`${goal(id)}/replan`),
};
